import { useAsyncState } from '@vueuse/core'

import { FunCoinRewardType, type OrderDetail } from '@/view-models/order'
import { ResponseCode } from '@/view-models/common'

import { useIntlNumber } from '@/composition/useIntl'
import * as paymentService from '@/service/payment'
import type { FmemberFunCoinDetail } from '@/types/api'

export type FunCoinData = Partial<
  Pick<
    OrderDetail,
    'fun_rewards_type' | 'fun_rewards_per' | 'fun_rewards_fixed' | 'comment_reward' | 'comment_reward_amount' | 'symbol' | 'symbol_code'
  > & { currency_code: string }
>

export function useFunCoins(infoParam: MaybeRef<FunCoinData>) {
  const info = computed(() => unref(infoParam || {}))
  const i18n = useI18n()
  const { getLocaleNumber } = useIntlNumber()

  const funCoinsRewardsText = computed(() => {
    if (info.value?.fun_rewards_type === FunCoinRewardType.Fixed) {
      return i18n.t('billing_fun_coin_back_reservation_only', { funcoin: getLocaleNumber(info.value.fun_rewards_fixed) })
    }

    const percentage = 100 * (info.value?.fun_rewards_per || 0)
    return `${percentage || 0}% ${i18n.t('billing_fun_coin_back')}`
  })

  /** fun coin 回饋 */
  const funCoinsFeedback = computed(() => {
    return info?.value?.comment_reward || 0
  })

  const funCoinsFeedbackText = computed(() => {
    if ((info.value.hasOwnProperty('symbol_code') || info.value.hasOwnProperty('currency_code')) && funCoinsFeedback.value) {
      return `+ ${getLocaleNumber(funCoinsFeedback.value)}`
    }
    return ''
  })

  /** fun coin 實際幣別回饋金額 */
  const funCoinsWithRateFeedback = computed(() => {
    return info?.value?.comment_reward_amount || 0
  })

  const funCoinsWithRateFeedbackText = computed(() => {
    const currency = info.value.symbol_code || info.value.currency_code || ''

    const res = i18n.t('funcoin_currency_value', {
      currency,
      amount: funCoinsWithRateFeedback.value,
    })

    return res
  })

  return {
    funCoinsRewardsText,
    funCoinsFeedback,
    funCoinsFeedbackText,
    funCoinsWithRateFeedback,
    funCoinsWithRateFeedbackText,
  }
}

export function useFunCoinsInfo() {
  const { $axios } = useNuxtApp()

  const { state, isLoading, executeImmediate } = useAsyncState(executor, null, {
    immediate: false,
  })

  async function executor(currencyId?: number) {
    if (typeof currencyId === 'undefined') {
      return null
    }

    const { code, data } = await paymentService.getFunCoinsInfo($axios, {
      currencyId,
    })

    if (code !== ResponseCode.Success || !data) {
      return null
    }

    return data as FmemberFunCoinDetail
  }

  return {
    state,
    isLoading,
    execute: executeImmediate,
  }
}
